import { ExerciseGameType } from '@/api/common';
import Exercise from '@/api/exercise';
import config from '@/config';
import store from '@/store';
import axios, { AxiosResponse } from 'axios';
import { isEmpty } from 'lodash';
import { safeLock } from '../common/util';
import { ISentenceCheck } from './common';
import {
  saveConversationContentIPA,
  saveListeningItemIPA,
  saveSpeakingContentIPA
} from './save-content';
import { checkContent } from '@/api/content-check';

export interface IReferenceScoreResponse {
  reference_score: {
    success: boolean;
    sentence_ipa: string;
    stream_score_type: string;
    error_message: string;
  };
}

async function checkAudioFile(
  audioUrl: string,
  sentence: string,
  transcript: string = '[]'
) {
  if (isEmpty(sentence)) {
    return {
      success: false,
      error_message: 'Empty Sentence',
      sentence_ipa: '',
      stream_score_type: ''
    };
  }

  if (isEmpty(audioUrl)) {
    return {
      success: false,
      error_message: 'Empty Audio',
      sentence_ipa: '',
      stream_score_type: ''
    };
  }

  const bodyFormData = new FormData();
  const ts = Date.now().toString();

  bodyFormData.append('sentence', sentence);
  bodyFormData.append('ts', ts.substring(0, ts.length - 3));
  bodyFormData.append('packet_file', audioUrl);
  bodyFormData.append('transcript', transcript);

  try {
    const response: AxiosResponse<IReferenceScoreResponse> = await axios({
      method: 'POST',
      url: config.API_ROOT + '/reference-score/',
      timeout: 5000, // Wait for 5 seconds
      data: bodyFormData,
      headers: {
        'X-Session': store.state.sessionToken
      }
    });

    return (
      response.data.reference_score || {
        success: false,
        error_message: 'Unknow Error'
      }
    );
  } catch (error) {
    return {
      success: false,
      error_message: 'Request Timeout',
      sentence_ipa: '',
      stream_score_type: ''
    };
  }
}

function getMessagePriority(data) {
  let result = '';
  for (const item of data) {
    if (item.error_level === 'ERROR') {
      result = 'ERROR';
      break;
    } else if (item.error_level === 'WARNING') {
      result = 'WARNING';
      break;
    } else {
      result = 'INFO';
    }
  }

  return result;
}

function getAudioStatus(sentence: ISentenceCheck) {
  const audios: string[] = [];
  if (!isEmpty(sentence.firstStatus)) {
    audios.push(sentence.firstStatus);
  }

  if (!isEmpty(sentence.secondStatus)) {
    audios.push(sentence.secondStatus);
  }

  let isCorrect = false;

  for (const item of audios) {
    if (item === 'correct') {
      isCorrect = true;
    } else {
      isCorrect = false;
      break;
    }
  }

  if (isCorrect) {
    return 'CORRECT';
  }

  let isWarning = false;

  for (const item of audios) {
    if (item === 'almost_correct') {
      isWarning = true;
    } else if (item !== 'correct') {
      isWarning = false;
      break;
    }
  }

  if (isWarning) {
    return 'WARNING';
  }

  return 'ERROR';
}

function processSpeechResponse(responseContentCheck) {
  return responseContentCheck.result.map((item) => ({
    error_level: item.error_level,
    message: item.message,
    word: item.word,
    sentence: item.sentence
  }));
}

export async function checkListeningGame(
  exercise: Exercise,
  sentence: ISentenceCheck
) {
  // Check content is correct or not
  const responseContentCheck = await performContentCheck(exercise.id);
  if (isEmpty(responseContentCheck.result)) {
    sentence.contentCheck = 'CORRECT';
  } else {
    const messageItems = processSpeechResponse(responseContentCheck);
    sentence.contentCheck = getMessagePriority(messageItems);
    sentence.errorLog = messageItems;
  }

  const listeningItems = exercise.listening_content.items;

  if (isEmpty(listeningItems)) {
    sentence.processing = 'Listening Game has no items';
    return;
  }

  if (listeningItems.length !== 2) {
    sentence.processing = 'Listening Game must have exact 2 items';
    return;
  }

  sentence.processing = 'Saving IPA';
  const firstItemResult = await checkAudioFile(
    listeningItems[0].audio_link,
    listeningItems[0].sentence
  );

  const secondItemResult = await checkAudioFile(
    listeningItems[1].audio_link,
    listeningItems[1].sentence
  );

  if (firstItemResult.success && !isEmpty(firstItemResult.sentence_ipa)) {
    await safeLock(Exercise.SLUG, exercise.id, () =>
      saveListeningItemIPA(listeningItems[0], firstItemResult.sentence_ipa)
    );

    sentence.firstIPA = firstItemResult.sentence_ipa;
    sentence.firstStatus = firstItemResult.stream_score_type;
  } else {
    sentence.firstStatus = firstItemResult.error_message;
  }

  if (secondItemResult.success && !isEmpty(secondItemResult.sentence_ipa)) {
    await safeLock(Exercise.SLUG, exercise.id, () =>
      saveListeningItemIPA(listeningItems[1], secondItemResult.sentence_ipa)
    );
    sentence.secondIPA = secondItemResult.sentence_ipa;
    sentence.secondStatus = secondItemResult.stream_score_type;
  } else {
    sentence.secondStatus = secondItemResult.error_message;
  }

  sentence.audio = getAudioStatus(sentence);
  sentence.processing =
    firstItemResult.success && secondItemResult.success ? 'Done' : 'Error';
}

export async function checkSpeakingGame(
  exercise: Exercise,
  sentence: ISentenceCheck
) {
  // Check content is correct or not
  const responseContentCheck = await performContentCheck(exercise.id);
  if (isEmpty(responseContentCheck.result)) {
    sentence.contentCheck = 'CORRECT';
  } else {
    const messageItems = processSpeechResponse(responseContentCheck);
    sentence.contentCheck = getMessagePriority(messageItems);
    sentence.errorLog = messageItems;
  }

  sentence.processing = 'Checking';
  const speaking = exercise.speaking_content;
  const result = await checkAudioFile(
    speaking.audio_link,
    speaking.sentence,
    JSON.stringify(speaking.transcript_arpabet)
  );

  if (result.success && !isEmpty(result.sentence_ipa)) {
    sentence.processing = 'Saving IPA';

    await safeLock(Exercise.SLUG, exercise.id, () =>
      saveSpeakingContentIPA(speaking, result.sentence_ipa)
    );
  }

  if (result.success) {
    sentence.secondIPA = result.sentence_ipa;
    sentence.secondStatus = result.stream_score_type;
  } else {
    sentence.secondStatus = result.error_message;
  }

  sentence.audio = getAudioStatus(sentence);
  sentence.processing = result.success ? 'Done' : 'Error';
}

export async function checkConversationGame(
  exercise: Exercise,
  sentence: ISentenceCheck
) {
  // Check content is correct or not
  const responseContentCheck = await performContentCheck(exercise.id);
  if (isEmpty(responseContentCheck.result)) {
    sentence.contentCheck = 'CORRECT';
  } else {
    const messageItems = processSpeechResponse(responseContentCheck);
    sentence.contentCheck = getMessagePriority(messageItems);
    sentence.errorLog = messageItems;
  }

  sentence.processing = 'Checking';
  const speaking = exercise.speaking_content;
  const elsa = exercise.conversation_content;

  const speakingResult = await checkAudioFile(
    speaking.audio_link,
    speaking.sentence
  );

  const elsaResult = await checkAudioFile(elsa.audio_link, elsa.sentence);

  if (elsaResult.success && !isEmpty(elsaResult.sentence_ipa)) {
    sentence.processing = 'Saving IPA';
    await safeLock(Exercise.SLUG, exercise.id, () =>
      saveConversationContentIPA(elsa, elsaResult.sentence_ipa)
    );

    sentence.firstIPA = elsaResult.sentence_ipa;
    sentence.firstStatus = elsaResult.stream_score_type;
  } else {
    sentence.firstStatus = elsaResult.error_message;
  }

  if (speakingResult.success && !isEmpty(speakingResult.sentence_ipa)) {
    await safeLock(Exercise.SLUG, exercise.id, () =>
      saveSpeakingContentIPA(speaking, speakingResult.sentence_ipa)
    );

    sentence.secondIPA = speakingResult.sentence_ipa;
    sentence.secondStatus = speakingResult.stream_score_type;
  } else {
    sentence.secondStatus = speakingResult.error_message;
  }

  sentence.audio = getAudioStatus(sentence);
  sentence.processing =
    elsaResult.success && speakingResult.success ? 'Done' : 'Error';
}

async function performContentCheck(exerciseId) {
  const data = await Exercise.getJson(exerciseId);
  const result = await checkContent(data);

  return result;
}

export async function checkExercise(sentence: ISentenceCheck) {
  sentence.contentCheck = 'Checking';
  let exercise = null;
  let gameType = null;

  try {
    exercise = await Exercise.get(Number.parseInt(sentence.exerciseId));
    gameType = exercise.game_type;
  } catch (e) {
    console.log(e.response);

    sentence.contentCheck = 'ERROR';
    sentence.audio = 'ERROR';
    sentence.processing = 'ERROR';
    return;
  }

  if (
    gameType === ExerciseGameType.PRONUNCIATION ||
    gameType === ExerciseGameType.WORD_STRESS ||
    gameType === ExerciseGameType.SENTENCE_STRESS
  ) {
    await checkSpeakingGame(exercise, sentence);
    return;
  }

  if (
    gameType === ExerciseGameType.CONVERSATION ||
    gameType === ExerciseGameType.VIDEO_CONVERSATION
  ) {
    await checkConversationGame(exercise, sentence);
    return;
  }

  if (
    gameType === ExerciseGameType.LISTEN_TEXT2AUDIO ||
    gameType === ExerciseGameType.LISTEN_AUDIO2TEXT
  ) {
    await checkListeningGame(exercise, sentence);
    return;
  }

  sentence.processing = 'Done';
}
